/**
 * L'Chaim Dashboard — Distressed Blueprint Edition.
 *
 * Visual language: royal-blue blueprint paper with grid + grain, white
 * brittle borders, parchment cards for daily content, cyan accents on
 * L'CHAIM type, red signal for Mark-Day-Complete. Inspired by Brandon's
 * reference mockup.
 *
 * NEW vs v1:
 *  - Week Pillar Overview header — pulled from chord PDF "THE PILLAR
 *    OVERVIEW / CORE LOGIC" block, displayed atop every day in that week
 *  - Previous-Day navigator (← / →) — non-destructive, doesn't mutate
 *    server progress, just lets the scholar revisit recent days
 *  - Vault link routes to /academy/lchaim/vault (real page, no more 404)
 *  - More elite language: "THE WORD", "THE CHARGE" (was assignment), etc.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  BookOpen, Check, Lock, ChevronLeft, ChevronRight,
  Save, Loader2, Cloud, Archive, ArrowLeft, Quote, Shield,
  Share2, Download, Copy, Twitter, Facebook, Linkedin, X,
  ChevronDown, Compass, Target, ScrollText, ListChecks,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import CoachBSpeak from '../components/CoachBSpeak';

const COLORS = {
  blueprintBg: '#0c2243',
  blueprintGrid: 'rgba(255,255,255,0.08)',
  cyan: '#00d4ff',
  red: '#dc2626',
  parchment: '#f1ead7',
  parchmentDark: '#cdc4a8',
  ink: '#1a2333',
  gray: '#9ca3af',
};

const LChaimLogo = ({ size = 'medium' }) => {
  const fs = size === 'large' ? 'text-5xl' : size === 'medium' ? 'text-3xl' : 'text-xl';
  return (
    <span className={`${fs} font-black tracking-tight uppercase`} style={{
      textShadow: '0 2px 0 rgba(0,0,0,0.3)',
      letterSpacing: '0.02em',
    }}>
      <span style={{ color: '#fff' }}>L&apos;CHA</span>
      <span style={{ color: COLORS.cyan }}>IM</span>
    </span>
  );
};

// Distressed blueprint backdrop: grid lines + diagonal grain + white frame
const blueprintBg = {
  backgroundColor: COLORS.blueprintBg,
  backgroundImage: `
    linear-gradient(${COLORS.blueprintGrid} 1px, transparent 1px),
    linear-gradient(90deg, ${COLORS.blueprintGrid} 1px, transparent 1px),
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.05), transparent 60%),
    radial-gradient(circle at 70% 80%, rgba(0,212,255,0.03), transparent 60%)
  `,
  backgroundSize: '32px 32px, 32px 32px, 100% 100%, 100% 100%',
};

const parchmentBg = {
  backgroundColor: COLORS.parchment,
  backgroundImage: `
    radial-gradient(circle at 20% 30%, rgba(0,0,0,0.06), transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(0,0,0,0.04), transparent 50%),
    repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(0,0,0,0.015) 12px, rgba(0,0,0,0.015) 13px)
  `,
  color: COLORS.ink,
};

const LChaimDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progressDay, setProgressDay] = useState(1);    // server-tracked current
  const [progressChord, setProgressChord] = useState(1);
  const [viewingDay, setViewingDay] = useState(1);      // what the user is reading
  const [viewingChord, setViewingChord] = useState(1);
  const [dayState, setDayState] = useState({
    content: null, theme: '', weekPillar: null, globalWeek: 1, weekIntro: null, isPillarStart: false,
  });
  const todayContent = dayState.content;
  const chordTheme = dayState.theme;
  const weekPillar = dayState.weekPillar;
  const globalWeek = dayState.globalWeek;
  const weekIntro = dayState.weekIntro;
  const isPillarStart = dayState.isPillarStart;
  const [chordLocked, setChordLocked] = useState(false);
  const [journalEntry, setJournalEntry] = useState('');
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('academy_token');
      if (!token) {
        navigate('/academy/lchaim/login');
        return;
      }
      const profileRes = await api.get('/academy/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = profileRes.data.user;
      setUser(userData);

      const progRes = await api.get('/academy/lchaim/progress', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = progRes.data;
      setProgressDay(d.current_day || 1);
      setProgressChord(d.current_chord || 1);
      setViewingDay(d.current_day || 1);
      setViewingChord(d.current_chord || 1);
      setChordLocked(d.chord_locked || false);
      await loadDayContent(d.current_chord || 1, d.current_day || 1);
    } catch (e) {
      toast.error('Failed to load L\u2019Chaim dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadDayContent = (chord, day) => {
    const dayInChord = ((day - 1) % 35) + 1;
    return api
      .get(`/academy/lchaim/content/${chord}/day/${dayInChord}`)
      .then((res) => {
        if (res.data.success) {
          setDayState({
            content: res.data.day,
            theme: res.data.theme || '',
            weekPillar: res.data.week_pillar || null,
            globalWeek: res.data.week || 1,
            weekIntro: res.data.week_intro || null,
            isPillarStart: !!res.data.is_pillar_start,
          });
        }
      })
      .catch(() => {
        setDayState({
          content: { day, title: `Day ${day}`, the_word: 'Content loading...' },
          theme: '', weekPillar: null, globalWeek: 1, weekIntro: null, isPillarStart: false,
        });
      });
  };

  const jumpToDay = async (newDay) => {
    if (newDay < 1 || newDay > 350) return;
    const newChord = Math.ceil(newDay / 35);
    setViewingDay(newDay);
    setViewingChord(newChord);
    await loadDayContent(newChord, newDay);
  };

  const handleSaveJournal = async () => {
    if (!journalEntry.trim()) {
      toast.error('Write something in your reflection first.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('academy_token');
      await api.post('/academy/lchaim/journal', {
        day: viewingDay, chord: viewingChord, entry: journalEntry,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Reflection saved to your sentry log.');
      setJournalEntry('');
    } catch (e) {
      toast.error('Failed to save reflection');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkDayComplete = async () => {
    if (viewingDay !== progressDay) {
      toast.error('You can only complete your current day. Use ← → to read past days.');
      return;
    }
    try {
      const token = localStorage.getItem('academy_token');
      const res = await api.post('/academy/lchaim/complete-day', {
        day: viewingDay, chord: viewingChord, entry: journalEntry,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Day completed. Advancing to tomorrow.');
      setJournalEntry('');
      if (res.data?.next_day) {
        const nd = res.data.next_day;
        const nc = res.data.next_chord || viewingChord;
        setProgressDay(nd); setProgressChord(nc);
        setViewingDay(nd); setViewingChord(nc);
        await loadDayContent(nc, nd);
      }
    } catch (e) {
      toast.error('Failed to complete day');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={blueprintBg}>
        <Loader2 className="w-10 h-10 animate-spin" style={{ color: COLORS.cyan }} />
      </div>
    );
  }

  const dayInChord = ((viewingDay - 1) % 35) + 1;
  const weekInChord = Math.ceil(dayInChord / 7);
  const totalDays = 350;
  const daysRemaining = Math.max(0, totalDays - progressDay);
  const isViewingHistory = viewingDay < progressDay;
  const canGoBack = viewingDay > 1;
  const canGoFwd = viewingDay < progressDay;

  return (
    <div className="min-h-screen text-white relative" style={blueprintBg} data-testid="lchaim-dashboard">
      {/* Distressed white frame — torn-edge effect via dashed border + offset shadows */}
      <div className="absolute inset-3 sm:inset-5 pointer-events-none border-2 border-white/35"
           style={{ boxShadow: 'inset 0 0 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)' }} />

      <div className="relative max-w-3xl mx-auto px-5 sm:px-10 py-6 pb-32">
        {/* ===== Header ===== */}
        <div className="flex items-start justify-between mb-6">
          <Button variant="ghost" size="sm" className="text-white/70 hover:text-white -ml-2"
                  onClick={() => navigate('/academy/school')} data-testid="lchaim-saa-home-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> SAA School
          </Button>
          <Button variant="ghost" size="sm" className="text-white/70 hover:text-white -mr-2"
                  onClick={() => navigate('/academy/lchaim/vault')} data-testid="lchaim-vault-btn">
            <Archive className="w-4 h-4 mr-1.5" /> Vault
          </Button>
        </div>

        <div className="text-center mb-6">
          <LChaimLogo size="large" />
          <div className="mt-3 text-[10px] tracking-[0.4em] text-white/55 uppercase">
            Welcome back, {user?.name?.split(' ')[0] || 'Scholar'}
          </div>
        </div>

        {/* Progress strip */}
        <div className="grid grid-cols-3 gap-2 mb-7 text-center">
          <Stat label="Day" value={`${progressDay}/350`} />
          <Stat label="Chord / Week" value={`${progressChord} · W${weekInChord}/5`} />
          <Stat label="Days Left" value={daysRemaining} />
        </div>

        {/* ===== Week Pillar Overview ===== */}
        {weekPillar && (
          <WeekPillarPanel
            chord={viewingChord}
            theme={chordTheme}
            week={globalWeek}
            pillar={weekPillar}
          />
        )}

        {/* ===== This Week's Pillar Overview (long-format intro) ===== */}
        {weekIntro && (
          <PillarOverviewPanel intro={weekIntro} week={globalWeek} defaultOpen={isPillarStart} />
        )}

        {/* ===== Day Navigator ===== */}
        <div className="flex items-center justify-between mb-4 mt-8">
          <button
            onClick={() => jumpToDay(viewingDay - 1)}
            disabled={!canGoBack}
            className="flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase font-bold text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="prev-day-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 text-center">
            {isViewingHistory && <span className="text-[#ffb547]">Reading Past Day · </span>}
            Day {viewingDay} of 350
          </div>
          <button
            onClick={() => jumpToDay(viewingDay + 1)}
            disabled={!canGoFwd}
            className="flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase font-bold text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="next-day-btn"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* ===== Daily Content Card — parchment style ===== */}
        {todayContent && (
          <div className="relative shadow-2xl" data-testid="lchaim-day-card">
            <div className="absolute inset-0 transform rotate-[0.3deg]"
                 style={{ ...parchmentBg, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }} />
            <div className="relative px-7 py-8" style={parchmentBg}>
              {/* Share button — top right corner of the parchment */}
              <button
                onClick={() => setShareOpen(true)}
                className="absolute top-4 right-4 flex items-center gap-1.5 border-2 border-current px-2.5 py-1.5 text-[10px] font-black tracking-[0.2em] uppercase hover:bg-black/5 transition-colors"
                style={{ color: COLORS.ink }}
                data-testid="lchaim-share-btn"
                title="Print or share today's card"
              >
                <Share2 className="w-3 h-3" /> Share
              </button>

              <div className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: COLORS.ink }}>
                Day {viewingDay} — {todayContent.pillar || 'Reflection'}
              </div>
              <h2 className="mt-2 text-2xl sm:text-3xl font-black uppercase tracking-tight" style={{ color: COLORS.ink }}>
                {todayContent.title || `Day ${viewingDay}`}
              </h2>
              {/* Coach B reads today's full lesson aloud */}
              <div className="mt-3" data-testid="lchaim-day-voice">
                <CoachBSpeak
                  text={(() => {
                    const parts = [];
                    if (todayContent.title) parts.push(`${todayContent.title}.`);
                    if (todayContent.scripture) {
                      const ref = todayContent.scripture_ref ? ` — ${todayContent.scripture_ref}` : '';
                      parts.push(`Scripture${ref}. ... ${todayContent.scripture} ...`);
                    }
                    if (todayContent.the_word) parts.push(`The Word. ... ${todayContent.the_word} ...`);
                    if (todayContent.reflection) parts.push(`Reflection. ... ${todayContent.reflection} ...`);
                    if (todayContent.activity) parts.push(`The Charge. ... ${todayContent.activity}`);
                    return parts.join(' ').slice(0, 480);
                  })()}
                  label="Hear Coach B read today's lesson"
                  variant="full"
                  accent={COLORS.gold || '#D4AF37'}
                  testId="lchaim-day-coach-b"
                />
              </div>
              <div className="h-px my-5"
                   style={{ background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.4), transparent)' }} />

              {todayContent.scripture && (
                <Section label="Scripture">
                  <p className="text-base leading-relaxed italic" style={{ color: COLORS.ink }}>
                    &ldquo;{todayContent.scripture}&rdquo;
                    {todayContent.scripture_ref && (
                      <span className="not-italic font-bold ml-2 text-sm" style={{ color: COLORS.ink }}>
                        ({todayContent.scripture_ref})
                      </span>
                    )}
                  </p>
                </Section>
              )}

              {todayContent.the_word && (
                <Section label="The Word">
                  <p className="text-sm leading-relaxed" style={{ color: COLORS.ink }}>
                    {todayContent.the_word}
                  </p>
                </Section>
              )}

              {todayContent.reflection && (
                <Section label="Reflection">
                  <p className="text-sm leading-relaxed" style={{ color: COLORS.ink }}>
                    {todayContent.reflection}
                  </p>
                </Section>
              )}

              {todayContent.activity && (
                <Section label="The Charge">
                  <p className="text-sm leading-relaxed" style={{ color: COLORS.ink }}>
                    {todayContent.activity}
                  </p>
                </Section>
              )}

              {todayContent.assignment && todayContent.assignment !== todayContent.activity && (
                <Section label="Assignment">
                  <p className="text-sm leading-relaxed" style={{ color: COLORS.ink }}>
                    {todayContent.assignment}
                  </p>
                </Section>
              )}
            </div>
          </div>
        )}

        {/* ===== Sentry's Log ===== */}
        <div className="mt-8 border-2 p-5" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4" style={{ color: COLORS.cyan }} />
            <h3 className="text-xs tracking-[0.3em] uppercase font-black" style={{ color: COLORS.cyan }}>
              The Sentry&apos;s Log
            </h3>
          </div>
          <Textarea
            value={journalEntry}
            onChange={(e) => setJournalEntry(e.target.value)}
            placeholder="Document the work. What did the charge reveal? What boundary did you re-set today?"
            className="min-h-[120px] border-0 text-sm"
            style={parchmentBg}
            data-testid="lchaim-journal-textarea"
          />
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button
              onClick={handleSaveJournal}
              disabled={saving}
              variant="outline"
              className="border-white/30 bg-transparent text-white hover:bg-white/10 font-bold text-[11px] tracking-[0.2em] uppercase"
              data-testid="lchaim-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save
            </Button>
            <Button
              onClick={() => navigate('/academy/lchaim/vault')}
              variant="outline"
              className="border-white/30 bg-transparent text-white hover:bg-white/10 font-bold text-[11px] tracking-[0.2em] uppercase"
              data-testid="lchaim-open-vault-btn"
            >
              <Archive className="w-4 h-4 mr-1.5" /> Vault
            </Button>
          </div>
          {!isViewingHistory && (
            <Button
              onClick={handleMarkDayComplete}
              className="w-full mt-3 font-black text-xs tracking-[0.25em] uppercase border-0"
              style={{ background: COLORS.red, color: '#fff' }}
              data-testid="lchaim-mark-complete-btn"
            >
              <Cloud className="w-4 h-4 mr-2" /> Mark Day Complete
            </Button>
          )}
        </div>
      </div>

      {/* ===== Share Modal ===== */}
      {shareOpen && (
        <ShareDayModal
          chord={viewingChord}
          day={viewingDay}
          dayTitle={todayContent?.title}
          quote={weekPillar?.quote}
          pillar={todayContent?.pillar}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
};

// ===== Share Modal =====
const ShareDayModal = ({ chord, day, dayTitle, quote, pillar, onClose }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const pdfUrl = `${backendUrl}/api/academy/lchaim/day-card/${chord}/day/${day}.pdf`;
  const shareUrl = `${window.location.origin}/academy/lchaim/dashboard`;
  const shareText = `Day ${day} of my L'Chaim journey: "${dayTitle}" — ${pillar || ''}${quote ? ` · "${quote}"` : ''} · #PRSVR #LChaim`;

  const downloadPdf = () => {
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `lchaim-day-${day}.pdf`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('PDF opened — save or print from there');
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      toast.error('Sharing not supported in this browser. Use the buttons below.');
      return;
    }
    try {
      await navigator.share({ title: `L'Chaim Day ${day}`, text: shareText, url: shareUrl });
    } catch (_) { /* user cancelled */ }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Copied to clipboard — paste anywhere');
    } catch (_) {
      toast.error('Copy failed');
    }
  };

  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
  const ln = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&summary=${encodeURIComponent(shareText)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         onClick={onClose} data-testid="share-modal">
      <div className="w-full max-w-[420px] relative" onClick={(e) => e.stopPropagation()}
           style={parchmentBg}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-black/10"
                aria-label="Close" style={{ color: COLORS.ink }}>
          <X className="w-4 h-4" />
        </button>
        <div className="px-7 py-7" style={{ color: COLORS.ink }}>
          <div className="text-[10px] font-black tracking-[0.35em] uppercase" style={{ color: COLORS.ink }}>
            Share Today&apos;s Charge
          </div>
          <h3 className="mt-2 text-xl font-black uppercase tracking-tight">
            Day {day} · {dayTitle}
          </h3>
          {quote && (
            <p className="mt-3 italic text-sm leading-relaxed border-l-2 pl-3"
               style={{ borderColor: COLORS.red, color: COLORS.ink }}>
              &ldquo;{quote}&rdquo;
            </p>
          )}

          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-2 mt-6">
            <button
              onClick={downloadPdf}
              className="flex items-center justify-center gap-2 py-3 font-black text-xs tracking-[0.2em] uppercase border-2"
              style={{ borderColor: COLORS.ink, color: COLORS.ink }}
              data-testid="share-download-pdf"
            >
              <Download className="w-3.5 h-3.5" /> Print / PDF
            </button>
            <button
              onClick={nativeShare}
              className="flex items-center justify-center gap-2 py-3 font-black text-xs tracking-[0.2em] uppercase"
              style={{ background: COLORS.red, color: '#fff' }}
              data-testid="share-native"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>

          {/* Socials */}
          <div className="mt-5">
            <div className="text-[9px] font-black tracking-[0.3em] uppercase mb-2" style={{ color: COLORS.ink }}>
              Post Directly
            </div>
            <div className="grid grid-cols-4 gap-2">
              <a href={tw} target="_blank" rel="noreferrer"
                 className="flex items-center justify-center py-3 border-2 hover:bg-black/5"
                 style={{ borderColor: COLORS.ink, color: COLORS.ink }}
                 data-testid="share-twitter" title="Share on X / Twitter">
                <Twitter className="w-4 h-4" />
              </a>
              <a href={fb} target="_blank" rel="noreferrer"
                 className="flex items-center justify-center py-3 border-2 hover:bg-black/5"
                 style={{ borderColor: COLORS.ink, color: COLORS.ink }}
                 data-testid="share-facebook" title="Share on Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={ln} target="_blank" rel="noreferrer"
                 className="flex items-center justify-center py-3 border-2 hover:bg-black/5"
                 style={{ borderColor: COLORS.ink, color: COLORS.ink }}
                 data-testid="share-linkedin" title="Share on LinkedIn">
                <Linkedin className="w-4 h-4" />
              </a>
              <button onClick={copyText}
                 className="flex items-center justify-center py-3 border-2 hover:bg-black/5"
                 style={{ borderColor: COLORS.ink, color: COLORS.ink }}
                 data-testid="share-copy" title="Copy to clipboard">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-5 text-[10px] leading-relaxed opacity-60">
            The PDF is a printable parchment card. Share the link to send the live dashboard;
            attach the PDF to socials by saving it first.
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== Sub-components =====
const Stat = ({ label, value }) => (
  <div className="border border-white/15 px-2 py-2.5 bg-white/[0.03]">
    <div className="text-[8px] tracking-[0.3em] uppercase text-white/50">{label}</div>
    <div className="mt-1 text-base font-black font-mono">{value}</div>
  </div>
);

const Section = ({ label, children }) => (
  <div className="mt-5">
    <div className="text-[9px] font-black tracking-[0.35em] uppercase mb-2"
         style={{ color: COLORS.ink }}>
      {label}
    </div>
    {children}
  </div>
);

const WeekPillarPanel = ({ chord, theme, week, pillar }) => (
  <div className="border-2 border-white/25 p-5 mb-4 relative"
       style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.2))' }}
       data-testid="lchaim-week-pillar">
    <div className="flex items-start gap-3 mb-3">
      <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: COLORS.cyan }} />
      <div className="flex-1 min-w-0">
        <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-white/55">
          Chord {String(chord).padStart(2, '0')}
        </div>
        <div className="text-base font-black uppercase tracking-tight leading-tight">
          {theme}
        </div>
        <div className="mt-2 text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: COLORS.cyan }}>
          Week {week} · {pillar.pillar} ({pillar.subtitle})
        </div>
      </div>
    </div>

    {pillar.quote && (
      <div className="flex gap-2.5 items-start my-4 border-l-2 pl-3" style={{ borderColor: COLORS.cyan }}>
        <Quote className="w-3.5 h-3.5 mt-1 flex-shrink-0" style={{ color: COLORS.cyan }} />
        <p className="text-sm italic leading-relaxed text-white/90">&ldquo;{pillar.quote}&rdquo;</p>
      </div>
    )}

    {pillar.core_logic?.length > 0 && (
      <div>
        <div className="text-[9px] font-black tracking-[0.3em] uppercase mb-2" style={{ color: COLORS.cyan }}>
          The Core Logic
        </div>
        <ul className="space-y-2">
          {pillar.core_logic.map((b, i) => (
            <li key={i} className="flex gap-2 text-xs leading-relaxed text-white/85">
              <span style={{ color: '#fff' }}>●</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export const PillarOverviewPanel = ({ intro, week, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  useEffect(() => { setOpen(!!defaultOpen); }, [week, defaultOpen]);

  const Section = ({ icon: Icon, label, children }) => (
    <div className="mt-4">
      <div className="flex items-center gap-2 text-[9px] font-black tracking-[0.3em] uppercase mb-2" style={{ color: COLORS.cyan }}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      {children}
    </div>
  );
  const Bullets = ({ items }) => (
    <ul className="space-y-1.5">
      {items.map((b, i) => (
        <li key={i} className="flex gap-2 text-xs leading-relaxed text-white/85">
          <span style={{ color: COLORS.cyan }}>●</span><span>{b}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="mt-6 rounded-lg border border-white/10 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, rgba(0,212,255,0.06), rgba(0,0,0,0.25))' }}
      data-testid="lchaim-pillar-overview">
      {/* Persistent header — the Order (rhyming cadence) stays with them all week */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-5 pt-4 pb-4"
        data-testid="pillar-overview-toggle"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: COLORS.cyan }} />
            <div className="min-w-0">
              <div className="text-[9px] tracking-[0.3em] uppercase font-bold" style={{ color: COLORS.cyan }}>
                This Week's Pillar · Week {week}
              </div>
              <div className="text-base font-black uppercase tracking-tight leading-tight mt-0.5">
                {intro.title || `The Order of ${intro.pillar}`}
              </div>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: COLORS.cyan }} />
        </div>

        {/* The Order — always visible, even when collapsed */}
        {intro.order && (
          <div className="flex gap-2.5 items-start mt-3 border-l-2 pl-3" style={{ borderColor: COLORS.cyan }} data-testid="pillar-order-quote">
            <Quote className="w-3.5 h-3.5 mt-1 flex-shrink-0" style={{ color: COLORS.cyan }} />
            <p className="text-sm italic leading-relaxed text-white/90">&ldquo;{intro.order}&rdquo;</p>
          </div>
        )}
        {!open && (
          <div className="mt-2 text-[10px] tracking-[0.25em] uppercase font-bold text-white/45">
            Tap to read the full pillar teaching
          </div>
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 -mt-1" data-testid="pillar-overview-body">
          {(intro.focus || intro.objective) && (
            <Section icon={Target} label="The Weekly Order">
              {intro.focus && <p className="text-xs text-white/85 leading-relaxed"><span className="font-bold text-white">Focus:</span> {intro.focus}</p>}
              {intro.objective && <p className="text-xs text-white/85 leading-relaxed mt-1"><span className="font-bold text-white">Objective:</span> {intro.objective}</p>}
            </Section>
          )}
          {intro.deep_dive_verse && (
            <Section icon={ScrollText} label={`Deep Dive${intro.deep_dive_ref ? ` · ${intro.deep_dive_ref}` : ''}`}>
              <p className="text-xs italic text-white/85 leading-relaxed">&ldquo;{intro.deep_dive_verse}&rdquo;</p>
            </Section>
          )}
          {intro.core_logic?.length > 0 && (
            <Section icon={Compass} label="The Core Logic"><Bullets items={intro.core_logic} /></Section>
          )}
          {intro.key_takeaways?.length > 0 && (
            <Section icon={ListChecks} label="Key Takeaways"><Bullets items={intro.key_takeaways} /></Section>
          )}
          {intro.how_to?.length > 0 && (
            <Section icon={Check} label="How To Make It Known"><Bullets items={intro.how_to} /></Section>
          )}
        </div>
      )}
    </div>
  );
};

export default LChaimDashboard;